import { useState, useEffect, useRef } from "react";
import { ref, onValue, get, child, off } from "firebase/database";
import { db } from "../../../configs/Firebase";
import { useSelector } from "react-redux";
import style from "./MentionAutocomplete.module.css";

const MentionAutocomplete = ({ 
  text, 
  cursorPosition, 
  onSelect, 
  onClose,
  orgId,
  users: providedUsers,
  selectedIndex: providedSelectedIndex,
  onIndexChange
}) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const listRef = useRef(null);
  
  // Use provided users if available, otherwise load from Firebase
  const allUsers = providedUsers || users;
  const currentSelectedIndex = providedSelectedIndex !== undefined ? providedSelectedIndex : selectedIndex;
  const setCurrentSelectedIndex = onIndexChange || setSelectedIndex;

  useEffect(() => {
    // Only load users if not provided
    if (providedUsers) {
      setFilteredUsers(providedUsers);
      return;
    }
    
    if (!orgId) return;

    const usersRef = ref(db, `userData/${orgId}/users/`);
    
    const unsubscribe = onValue(usersRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const usersList = [];
        for (let i in data) {
          if (data[i].id) {
            try {
              const profileSnapshot = await get(ref(db, `userData/${data[i].id}/profile`));
              const profile = profileSnapshot.val() || {};
              usersList.push({
                id: data[i].id,
                name: profile.displayName || data[i].name || "",
                email: profile.email || data[i].email || "",
                photoURL: profile.microsoftPhotoURL || null,
              });
            } catch (error) {
              console.error(`Error loading profile for user ${data[i].id}:`, error);
              usersList.push({
                id: data[i].id,
                name: data[i].name || "",
                email: data[i].email || "",
                photoURL: null,
              });
            }
          }
        }
        setUsers(usersList);
        setFilteredUsers(usersList);
      }
    });

    return () => {
      off(usersRef);
      unsubscribe();
    };
  }, [orgId, providedUsers]);

  useEffect(() => {
    // Extract search term from text after @ symbol
    if (text && cursorPosition !== null && !providedUsers) {
      const textBeforeCursor = text.substring(0, cursorPosition);
      const lastAtIndex = textBeforeCursor.lastIndexOf("@");
      
      if (lastAtIndex !== -1) {
        const term = textBeforeCursor.substring(lastAtIndex + 1).trim();
        setSearchTerm(term);
        
        // Filter users based on search term
        const filtered = allUsers.filter(user => {
          const nameMatch = user.name.toLowerCase().includes(term.toLowerCase());
          const emailMatch = user.email.toLowerCase().includes(term.toLowerCase());
          return nameMatch || emailMatch;
        });
        setFilteredUsers(filtered);
        setCurrentSelectedIndex(0);
      } else {
        setFilteredUsers([]);
      }
    }
  }, [text, cursorPosition, allUsers, providedUsers]);

  useEffect(() => {
    // Scroll selected item into view
    if (listRef.current && currentSelectedIndex >= 0) {
      const selectedItem = listRef.current.children[currentSelectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [currentSelectedIndex]);

  const handleKeyDown = (e) => {
    if (filteredUsers.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCurrentSelectedIndex((prev) => 
        prev < filteredUsers.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCurrentSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      handleSelect(filteredUsers[currentSelectedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const handleSelect = (user) => {
    if (user) {
      onSelect(user);
    }
  };

  if (filteredUsers.length === 0) {
    return null;
  }

  return (
    <div 
      className={style.mentionAutocomplete}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className={style.userList} ref={listRef}>
        {filteredUsers.map((user, index) => (
          <div
            key={user.id}
            className={`${style.userItem} ${index === currentSelectedIndex ? style.selected : ""}`}
            onClick={() => handleSelect(user)}
            onMouseEnter={() => setCurrentSelectedIndex(index)}
          >
            <div
              className={style.userAvatar}
              style={
                user.photoURL
                  ? {
                      backgroundImage: `url(${user.photoURL})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : {}
              }
            >
              {!user.photoURL && (
                <>
                  {user.name?.split(" ")[0]?.charAt(0) || user.email?.charAt(0) || "?"}
                  {user.name?.split(" ")[1]?.charAt(0) || ""}
                </>
              )}
            </div>
            <div className={style.userInfo}>
              <div className={style.userName}>{user.name || user.email}</div>
              {user.name && user.email && (
                <div className={style.userEmail}>{user.email}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MentionAutocomplete;

